import {
  ENDPOINTS,
  RescanCloudeqEntityRequestSchema,
  SetContractCloudeqEntityRequestSchema,
  type ListCloudeqEntitiesResponse,
  type RescanCloudeqEntityRequestInput,
  type RescanCloudeqEntityResponse,
  type SetContractCloudeqEntityRequest,
  type SetContractCloudeqEntityResponse,
} from '@clm/shared-types';

import { apiGet, apiPost } from './client';

/**
 * The cloudEQ entity registry and its two admin writes — CLM-91 (D-91-2,
 * D-91-4, D-91-7).
 *
 * `listCloudeqEntities` reads the five registry rows (`contract:read`, no audit
 * row) — the selects and the By-entity tiles take their names from here, never
 * from a constant, because the fifth entity's name is still to be confirmed
 * (Q-91-1). `setContractCloudeqEntity` is the admin override on one contract:
 * the store step's in-tenant scan places a contract when exactly one entity is
 * named; this call places one the scan could not (*Unassigned*), moves one, or
 * clears it (`entity_key: null`) so the next scan places it again. A later scan
 * never overwrites an admin value. `rescanCloudeqEntity` re-runs the pure scan
 * over the contracts already converted — `dry_run` reports, a real run writes
 * one audit row per contract whose value changes and none otherwise.
 *
 * Every body is validated `.strict()` before anything is sent; every response
 * is parsed, not asserted. None of these controls is disabled by role — a 403
 * from the API renders the surface's problem copy (CLM-82).
 */
const LIST = ENDPOINTS['list-cloudeq-entities'];
const SET = ENDPOINTS['set-contract-cloudeq-entity'];
const RESCAN = ENDPOINTS['rescan-cloudeq-entity'];

export async function listCloudeqEntities(signal?: AbortSignal): Promise<ListCloudeqEntitiesResponse> {
  const raw = await apiGet<unknown>(LIST.path, signal === undefined ? {} : { signal });
  return LIST.response.parse(raw);
}

export async function setContractCloudeqEntity(
  request: SetContractCloudeqEntityRequest,
  signal?: AbortSignal,
): Promise<SetContractCloudeqEntityResponse> {
  const body = SetContractCloudeqEntityRequestSchema.parse(request);
  const { contract_key, ...rest } = body;
  const path = SET.path.replace('{contract_key}', encodeURIComponent(contract_key));
  const raw = await apiPost<unknown>(path, rest, signal === undefined ? {} : { signal });
  return SET.response.parse(raw);
}

export async function rescanCloudeqEntity(
  request: RescanCloudeqEntityRequestInput,
  signal?: AbortSignal,
): Promise<RescanCloudeqEntityResponse> {
  const body = RescanCloudeqEntityRequestSchema.parse(request);
  const raw = await apiPost<unknown>(RESCAN.path, body, signal === undefined ? {} : { signal });
  return RESCAN.response.parse(raw);
}
